/**
 * The Strobe API is poorly documented, however, I did manage to find this:
 * https://github.com/denivip/StrobeMediaPlayback/blob/master/JSAPI.md
 */
import swfobject from 'swfobject';

import * as AudioConstants from '../../../constants';
import * as D from '../../../deps';

import StrobePlayerHelper from './strobePlayerHarnessHelper';

const strobeSWF = require('../../../../../../../vendor/StrobeMediaPlayback.swf');

export const NAME = AudioConstants.AudioHarnessName.STROBE;
const VOLUME_MULTIPLIER = 0.01;
const strobePlayerHelper = new StrobePlayerHelper();
const log = D.Logger.child({
  component: 'strobePlayerHarness',
});
// Expose sbStrobe providers in the global scope and reference them through that.
window.sbStrobePlayerHelper = strobePlayerHelper;
let count = 0;

export function initialize(audioSrc, resolve, reject) {
  const htmlId = `strobeAudioPlayer_${count}`;
  count++;

  const div = document.createElement('div');
  div.id = htmlId;
  div.height = 0;
  div.width = 0;
  document.body.appendChild(div);

  const flashOptions = {
    src: audioSrc.url,
    controlBarMode: 'none',
    streamType: 'liveOrRecorded',
    verbose: true, // debug only
    autoPlay: false,
    expandedBufferTime: 30,
    minContinuousPlayback: 30,
    javascriptCallbackFunction(playerId, eventName, data) {
      window.sbStrobePlayerHelper.callbackHandler(playerId, eventName, data);
    },
  };

  const params = {
    allowFullScreen: 'false',
  };

  strobePlayerHelper.registerDeferred(htmlId, { resolve, reject });

  // Embed the player SWF:

  swfobject.embedSWF(
    strobeSWF,
    htmlId,
    1,
    1,
    '10.1.0',
    {},
    flashOptions,
    params,
    { name: htmlId, class: 'strobeMediaPlayer' },
    function (event) {
      if (!event.success) {
        reject(event);
      }
    },
  );
}

export function mapMethods(strobePlayer) {
  return {
    play() {
      strobePlayer.play2();
    },

    pause() {
      // TODO: Re-examine in https://jira.savagebeast.com/browse/WEB-7457
      this._pausedVolume = strobePlayer.getVolume();
      this.setVolume(0); // You can't pause a live stream,
    },

    setMuted(toMute) {
      strobePlayer.setMuted(toMute);
    },

    setVolume(volume) {
      if (this._pausedVolume) {
        this._pausedVolume = null;
      }

      strobePlayer.setVolume(volume * VOLUME_MULTIPLIER);
    },

    setCurrentTime() {
      /* no-op - you can't set time on a live stream */
    },

    destroy() {
      const el = document.getElementById(strobePlayer.id);
      const parentNode = el.parentNode;
      if (parentNode) {
        parentNode.removeChild(el);
      }
    },

    getState() {
      const state = strobePlayer.getState();
      const timePlusBuffer = strobePlayer.getCurrentTime() + strobePlayer.getBufferLength();
      return {
        readyState: state !== 'uninitialized',
        currentTime: strobePlayer.getCurrentTime(),
        // multiplying by 10 to increase precision
        buffered:
          parseInt(
            Math.round(timePlusBuffer * 10) / Math.round(strobePlayer.getDuration() * 10),
            10,
          ) === 1,
        duration: strobePlayer.getDuration(),
        ended: strobePlayer.getCurrentTime() / strobePlayer.getDuration() === 1,
        error: state === 'playbackError',
        volume: strobePlayer.getVolume(),
        muted: strobePlayer.getMuted(),
        paused: strobePlayer.getPaused(),
        played: {}, // @todo
        src: strobePlayer.getSrc(),
      };
    },
  };
}

export function mapEventBindings(strobePlayer) {
  // Because of the way strobe player loads flash content, all strobe player logic ends up having to go into a global object.
  // Here we just pass our provider out to that global object.
  return {
    play(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'play', handlerFn);
    },

    pause(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'pause', handlerFn);
    },

    playing(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'playing', handlerFn);
    },

    stalled() {
      log.debug('stalled(): not supported for strobePlayerHarness');
    },

    timeupdate(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'timeupdate', handlerFn);
    },

    durationchange(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'durationchange', handlerFn);
    },

    loadedmetadata(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'loadedmetadata', handlerFn);
    },

    canplaythrough() {
      log.debug('canplaythrough(): not supported for strobePlayerHarness');
    },

    progress() {
      log.debug('progress(): not supported for strobePlayerHarness');
    },

    ended(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'ended', handlerFn);
    },

    seeked(handlerFn) {
      strobePlayerHelper.setEventHandler(strobePlayer.id, 'seeked', handlerFn);
    },

    error() {
      log.debug('error(): not supported for strobePlayerHarness');
    },

    playbackMetrics(handlerFn) {
      log.debug('playbackMetric(): not supported for strobePlayerHarness');
    },
  };
}

export function mapEventRemovers(strobePlayer) {
  return {
    play() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'play');
    },

    pause() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'pause');
    },

    playing() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'playing');
      strobePlayer.removeEventListener('playStateChange', 'onSBStrobePlayerStateChanged');
    },

    stalled() {
      log.debug('stalled(): not supported for strobePlayerHarness');
    },

    timeupdate() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'timeupdate');
    },

    durationchange() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'durationchange');
    },

    loadedmetadata() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'loadedmetadata');
    },

    canplaythrough() {
      log.debug('canplaythrough(): not supported for strobePlayerHarness');
    },

    progress() {
      log.debug('progress(): not supported for strobePlayerHarness');
    },

    ended() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'ended');
    },

    seeked() {
      strobePlayerHelper.removeEventHandler(strobePlayer.id, 'seeked');
    },

    error() {
      log.debug('error(): not supported for strobePlayerHarness');
    },

    playbackMetrics(handlerFn) {
      log.debug('playbackMetric(): not supported for strobePlayerHarness');
    },
  };
}
